<div [ngClass]="{ 'error': error }" class="datepicker-container">
  @if (label) {
    <label [attr.for]="inline || view === 'week' ? null : inputId" class="datepicker-label">{{ label }}@if (required) {<span class="required-mark" aria-hidden="true">*</span>}</label>
  }
  
  @if(view === 'week' && inline) {
    <div class="week-view">
      <div class="week-header">
        <div class="week-navigation">
          <button class="nav-button" [attr.aria-label]="'common.a11y.previousWeek' | transloco" (click)="previousWeek()">
            <lucide-icon name="ChevronLeft" aria-hidden="true"></lucide-icon>
          </button>
          @for(day of getWeekDays(); track day.date) {
            <div class="day-number" 
              [class.active]="isSelectedDay(day.date)"
              [class.today]="isToday(day.date)"
              (click)="selectDay(day.date)">
              {{ day.number }}
              @if(hasEvents(day.date)) {
                <span class="event-dot"></span>
              }
              @if(hasRequests(day.date)) {
                <span class="request-dot"></span>
              }
            </div>
          }
          <button class="nav-button" [attr.aria-label]="'common.a11y.nextWeek' | transloco" (click)="nextWeek()">
            <lucide-icon name="ChevronRight" aria-hidden="true"></lucide-icon>
          </button>
        </div>
      </div>
    </div>
  } @else if(inline) {
    <p-datepicker
      [(ngModel)]="value"
      [inline]="true"
      [showWeek]="false"
      [disabled]="disabled"
      [ngClass]="[size]"
      class="custom-datepicker"
      [selectionMode]="rangeMode ? 'range' : 'single'"
    >
      <ng-template pTemplate="date" let-date>
        <span [class.has-event]="hasEvents(date)" [class.has-request]="hasRequests(date)">{{date.day}}</span>
      </ng-template>
    </p-datepicker>
  } @else {
    @if(useSpecialContainer) {
      <!-- Inline calendar design for all absence types -->
      <div class="date-time-container">
        <div class="date-input-section">
          <input 
            type="text" 
            [id]="inputId"
            [attr.aria-required]="required || null"
            class="date-input"
            [placeholder]="placeholder"
            [value]="getFormattedDateRange()"
            readonly
            (click)="toggleCalendar()"
          >
          <lucide-icon name="Calendar" class="calendar-icon" (click)="toggleCalendar()"></lucide-icon>
        </div>
        
        @if(showCalendar) {
          <div class="inline-calendar-container">
            <p-datepicker
              [ngModel]="value"
              (ngModelChange)="onDateSelect($event)"
              [inline]="true"
              [showWeek]="false"
              [disabled]="disabled"
              class="custom-inline-datepicker"
              [selectionMode]="showTimeInputs ? 'single' : (rangeMode ? 'range' : 'single')"
            >
              <ng-template pTemplate="date" let-date>
                <span [class.has-event]="hasEvents(date)">{{date.day}}</span>
              </ng-template>
            </p-datepicker>
          </div>
        }
        
        <!-- Time inputs only visible when showTimeInputs is true -->
        @if(showTimeInputs) {
          <div class="time-inputs-section">
            <div class="time-row">
              <div class="time-field">
                <label class="time-label">{{ 'datepicker.time.start.label' | transloco }}</label>
                <div class="time-input-container">
                  <input 
                    type="text" 
                    class="time-input"
                    [value]="startTime"
                    readonly
                    (click)="openTimeSelector('start')"
                    [placeholder]="'datepicker.time.start.placeholder' | transloco"
                  >
                  <lucide-icon name="Calendar" class="time-icon"></lucide-icon>
                </div>
              </div>
              
              <div class="time-field">
                <label class="time-label">{{ 'datepicker.time.end.label' | transloco }}</label>
                <div class="time-input-container">
                  <input 
                    type="text" 
                    class="time-input"
                    [value]="endTime"
                    readonly
                    (click)="openTimeSelector('end')"
                    [placeholder]="'datepicker.time.end.placeholder' | transloco"
                  >
                  <lucide-icon name="Calendar" class="time-icon"></lucide-icon>
                </div>
              </div>
            </div>
            
            <!-- Inline Time Selector -->
            @if(showTimeSelector) {
              <div class="inline-time-selector">
                <p class="time-selector-title">{{ 'datepicker.time.selector.title' | transloco }}</p>
                
                <div class="time-spinner-container">
                  <div class="time-spinner">
                    <button class="spinner-btn" [attr.aria-label]="'common.a11y.increaseHour' | transloco" (click)="incrementHour()">
                      <lucide-icon name="ChevronUp"></lucide-icon>
                    </button>
                    <div class="spinner-value">{{ formatNumber(tempHour) }}</div>
                    <button class="spinner-btn" [attr.aria-label]="'common.a11y.decreaseHour' | transloco" (click)="decrementHour()">
                      <lucide-icon name="ChevronDown"></lucide-icon>
                    </button>
                  </div>
                  
                  <div class="time-separator">:</div>
                  
                  <div class="time-spinner">
                    <button class="spinner-btn" [attr.aria-label]="'common.a11y.increaseMinute' | transloco" (click)="incrementMinute()">
                      <lucide-icon name="ChevronUp"></lucide-icon>
                    </button>
                    <div class="spinner-value">{{ formatNumber(tempMinute) }}</div>
                    <button class="spinner-btn" [attr.aria-label]="'common.a11y.decreaseMinute' | transloco" (click)="decrementMinute()">
                      <lucide-icon name="ChevronDown"></lucide-icon>
                    </button>
                  </div>
                </div>
                
                <div class="time-selector-actions">
                  <button class="confirm-btn" (click)="confirmTimeSelection()">{{ 'datepicker.time.selector.confirm' | transloco }}</button>
                </div>
              </div>
            }
          </div>
        }
      </div>
    } @else {
      <!-- Regular popup datepicker -->
      <p-datepicker
        [(ngModel)]="value"
        [iconDisplay]="'input'"
        [showIcon]="true"
        [inputId]="inputId"
        [required]="required"
        [placeholder]="placeholder"
        [disabled]="disabled"
        [ngClass]="[size]"
        class="custom-datepicker"
        [selectionMode]="rangeMode ? 'range' : 'single'"
        (onSelect)="onDateSelect($event)"
        [appendTo]="appendTo"
      >
        <ng-template pTemplate="date" let-date>
          <span [class.has-event]="hasEvents(date)">{{date.day}}</span>
        </ng-template>
      </p-datepicker>
    }
  }

  @if(error){
    <div class="error-message">{{ errorMessage || ('common.messages.requiredField' | transloco) }}</div>
  }
</div>